import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, shareReplay, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { createActionResponseFactory } from '@core/ngrx-legacy';
import { ReportsResultsActions, ReportsResultsSelectors } from '@core/reports-data';
import { ReportResultsDataParams, ReportResultsParams } from '@core/reports-types';
import { buildApiDataParamsHash } from '@core/reports-util';

@Injectable({
  providedIn: 'root',
})
export class ReportsResultsFacade {
  private readonly store = inject(Store);
  private actionFactory = createActionResponseFactory(ReportsResultsActions);

  public areParamsEqual(a: ReportResultsDataParams, b: ReportResultsDataParams): boolean {
    return buildApiDataParamsHash(a) === buildApiDataParamsHash(b);
  }

  public getResultsMeta(params: ReportResultsDataParams): Observable<{
    isLoaded: boolean;
    isPending: boolean;
    isFailed: boolean;
    error: any;
  }> {
    const paramsHash = buildApiDataParamsHash(params);
    return this.store
      .select(ReportsResultsSelectors.meta(paramsHash))
      .pipe(filterBoolean, shareReplay(1));
  }

  public fetchResults(params: ReportResultsDataParams) {
    const payload: ReportResultsParams = {
      params,
      paramsHash: buildApiDataParamsHash(params),
    };

    this.store.dispatch(ReportsResultsActions.fetchOneResult({ payload }));
  }

  public fetchAndGetResultsContinously$(params: ReportResultsDataParams) {
    const data: ReportResultsParams = {
      params,
      paramsHash: buildApiDataParamsHash(params),
    };

    return this.dispatchLoadResults(data);
  }

  public getResults(params: ReportResultsDataParams) {
    const paramsHash = buildApiDataParamsHash(params);
    return this.store.select(ReportsResultsSelectors.byId(paramsHash)).pipe(
      filterBoolean,
      map(x => x.response),
    );
  }

  private dispatchLoadResults(payload: ReportResultsParams) {
    return this.actionFactory(
      ReportsResultsActions.fetchOneResult,
      {
        payload,
      },
      {
        identityCheck: x => x.payload.paramsHash === payload.paramsHash,
      },
    ).pipe(switchMap(() => this.getResults(payload.params)));
  }
}
